/**
 * Copyright (c) 2024 Securisea, Inc.
 * All rights reserved.
 *
 * This is unpublished, propriety, internal
 * source code of Securisea, Inc.
 * No license is provided to any third parties.
 * If found outside of a Securisea, Inc. environment
 * report to bounty@securisea.com for a possible cash reward
 * and promptly delete all files.
 */
import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import { Organization } from "../../types/organizations";
import {
  useGetOrganizationOrgAdmin,
  useUpdateOrgAdminOrganization,
} from "../../hooks/organizations";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../api/apiClient";

interface SlackManagerProps {
  organization_id: string;
}

export const SlackNotificationManager: React.FC<SlackManagerProps> = ({
  organization_id = "",
}) => {
  const [open, setOpen] = useState(false);

  const { data: organization, isLoading } = useQuery({
    queryKey: ["organization", organization_id],
    queryFn: () => useGetOrganizationOrgAdmin(organization_id)(),
    enabled: !!organization_id,
  });

  const [formData, setFormData] = useState({
    slack_webhook_url: organization?.slack_webhook_url || "",
    slack_enabled: organization?.slack_enabled || false,
  });

  useEffect(() => {
    setFormData({
      slack_webhook_url: organization?.slack_webhook_url || "",
      slack_enabled: organization?.slack_enabled || false,
    });
  }, [organization]);

  const [testing, setTesting] = useState(false);
  const [testSuccess, setTestSuccess] = useState<boolean | null>(null);

  const updateOrganization = useUpdateOrgAdminOrganization();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setTestSuccess(null);
    setFormData({
      slack_webhook_url: organization?.slack_webhook_url || "",
      slack_enabled: organization?.slack_enabled || false,
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "slack_enabled" ? checked : value,
    }));
  };

  const handleTestWebhook = async () => {
    setTesting(true);
    setTestSuccess(null);
    try {
      const response = await apiClient.post(
        `/api/manage-organization/test-slack/?id=${organization?.id}&slack_webhook_url=${formData?.slack_webhook_url}`
      );
      if (response.status === 200) {
        setTestSuccess(true);
        toast.success("Slack notification test successful!");
      }
    } catch (error) {
      setTestSuccess(false);
      toast.error("Failed to test Slack notification");
    } finally {
      setTesting(false);
    }
  };

  const handleSave = () => {
    if (organization) {
      updateOrganization.mutate({
        ...organization,
        ...formData,
      });
      handleClose();
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<NotificationsIcon />}
        onClick={handleOpen}
        size="small"
        sx={{ fontSize: 12 }}
      >
        Slack Notifications
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Slack Notifications - {organization?.name}</DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Webhook URL"
              name="slack_webhook_url"
              value={formData.slack_webhook_url}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
            />

            <FormControlLabel
              control={
                <Switch
                  disabled={!formData.slack_webhook_url}
                  checked={formData.slack_enabled}
                  onChange={handleInputChange}
                  name="slack_enabled"
                  color="primary"
                />
              }
              label="Enable Slack Notifications"
              sx={{ mt: 2 }}
            />

            <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleTestWebhook}
                disabled={!formData.slack_webhook_url || testing}
              >
                {testing ? <CircularProgress size={24} /> : "Test Notification"}
              </Button>

              {testSuccess !== null && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Checkbox
                    checked={testSuccess}
                    icon={<></>}
                    checkedIcon={<CheckIcon color="success" />}
                    disabled
                  />
                  <Typography
                    color={testSuccess ? "success.main" : "error.main"}
                  >
                    {testSuccess ? "Test Successful" : "Test Failed"}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              updateOrganization.isPending || !formData.slack_webhook_url
            }
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
